import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import api from '../../services/api';
import { useData } from '../../contexts/DataContext';

export default function BankFeedManager({ notify }) {
  const { fetchAccounts, fetchTransactions } = useData();
  const [data, setData] = useState(null);
  const [destinations, setDestinations] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(async () => {
    try { const res = await api.get('/akahu/feeds'); setData(res.data); setError(''); }
    catch (err) { setError(err.response?.data?.error || 'Could not load import choices.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!syncing) return undefined;
    let live = true;
    const timer = setInterval(async () => {
      try {
        const res = await api.get('/akahu/sync/status');
        if (live && !res.data?.inProgress) {
          setSyncing(false);
          await Promise.all([load(), fetchAccounts(), fetchTransactions({ silent: true })]);
          if (res.data?.lastSync?.status === 'failed') notify('Import choices are saved, but bank sync failed. Use Refresh & sync to retry.', 'warning');
        }
      } catch (_) { if (live) setSyncing(false); }
    }, 5000);
    return () => { live = false; clearInterval(timer); };
  }, [syncing, load, fetchAccounts, fetchTransactions, notify]);
  const save = async (feed, included, useForBalance = false) => {
    setBusy(true);
    try {
      await api.put(`/akahu/feeds/${feed.externalId}`, { included, useForBalance,
        localAccountId: feed.localAccountId || destinations[feed.externalId] || null });
      await Promise.all([load(), fetchAccounts()]);
      setSyncing(true);
      notify(included ? 'Import choice saved. A sync has been queued.' : 'Imports paused. Saved history is kept; bank access is unchanged.');
    } catch (err) { notify(err.response?.data?.error || 'Could not save import choice.', 'error'); }
    finally { setBusy(false); }
  };
  const undo = async () => {
    setBusy(true);
    try {
      await api.post(`/akahu/replacement/${data.latestMergeId}/undo`);
      await Promise.all([load(), fetchAccounts(), fetchTransactions({ silent: true })]);
      notify('The latest account combination was undone.');
    } catch (err) { notify(err.response?.data?.error || 'Could not undo combination.', 'error'); }
    finally { setBusy(false); }
  };
  return <Stack spacing={1.5}>
    <Typography variant="subtitle2">Accounts to import</Typography>
    {syncing && <Typography variant="caption">Bank sync queued or running…</Typography>}
    <Typography variant="body2" color="text.secondary">These choices control imports into Budget Clues. Use Manage bank accounts to change which accounts your bank shares. To combine a replacement card, open Accounts → edit the account → Replace a card.</Typography>
    {error && <Alert severity="warning" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>}
    {data?.items.map(feed => {
      const account = data.accounts.find(a => a.id === feed.localAccountId);
      const balanceSource = account?.balanceSourceId === feed.externalId;
      return <Box key={feed.externalId} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
        <Typography fontWeight={600} variant="body2">{feed.bank?.connection?.name} · {feed.name}{feed.bank?.formatted_account ? ` · …${feed.bank.formatted_account.replace(/\s/g, '').slice(-4)}` : ''}</Typography>
        <Typography variant="caption" color="text.secondary">{!feed.bank ? 'No longer shared' : feed.bank.status === 'INACTIVE' ? 'Needs reconnection — use Manage bank accounts' : 'Connected'}{account ? ` · Files into ${account.name}` : ' · Awaiting your choice'}</Typography>
        {!feed.localAccountId && feed.bank && <TextField select fullWidth size="small" label="Import destination" sx={{ mt: 1.5 }} value={destinations[feed.externalId] || ''} disabled={busy}
          onChange={e => setDestinations(d => ({ ...d, [feed.externalId]: e.target.value }))}>
          <MenuItem value="">Create a new Budget Clues account</MenuItem>
          {data.accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
        </TextField>}
        <Stack direction="row" alignItems="center" flexWrap="wrap">
          <FormControlLabel control={<Switch size="small" checked={feed.included} disabled={busy || !feed.bank} onChange={e => save(feed, e.target.checked)} />} label={<Typography variant="body2">Include in automatic imports</Typography>} />
          {feed.included && feed.bank && <Button size="small" disabled={busy || balanceSource} onClick={() => save(feed, true, true)}>{balanceSource ? 'Current balance source' : 'Use for current balance'}</Button>}
        </Stack>
      </Box>;
    })}
    {data?.latestMergeId && <Button disabled={busy} onClick={undo} sx={{ alignSelf: 'flex-start' }}>Undo latest account combination</Button>}
  </Stack>;
}
